import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useAuthorization } from "@common/providers";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import useFlags from "@common/hooks/useFlags";
import {
  useGetProjectByIdQuery,
  useListProjectPricingsQuery,
  useListProjectServicesQuery,
  useUpdateProjectMutation,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  capitalizeFirstLetter,
  formatUnprocessibleResponse,
  snakeToPascalCase,
  splitPascalCase,
  toSnakeCase,
} from "@utils/helperFunctions";
import Show from "./Show/Show";
import { MsPricingRow } from "./types/MsPricingRow";

interface Props {
  project_id: string;
  account_id: string;
}

const MsPricingV2Mspa: React.FC<Props> = ({ project_id, account_id }) => {
  const projectId = project_id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data: project, isLoading: projectLoading } = useGetProjectByIdQuery({
    id: projectId as any,
    slug: accountSlug,
  });

  const { data: pricingsData, isLoading: pricingsLoading } =
    useListProjectPricingsQuery({
      slug: accountSlug,
      id: Number(projectId),
    });

  const { data: servicesData, isLoading: servicesLoading } =
    useListProjectServicesQuery({
      slug: accountSlug,
      filter: { project: projectId, "service-type": "managed_services" },
      include: ["project-subservices", "project-subservices.resource"] as any,
      page: { size: 500 },
    });

  const authorized = useAuthorization();
  const { hasPrivilege } = usePrivilegeCheck();
  const flags = useFlags();
  const insightPupmEnabled = !!flags.releaseRecurringServiceCola;
  const pricingPrivilege = hasPrivilege("projects.pricing_adjustment", [
    "manage",
  ]);

  const [updateProject] = useUpdateProjectMutation();

  const [terms, setTerms] = useState("");
  const [startDate, setStartDate] = useState("");
  const [billingFrequency, setBillingFrequency] = useState("Monthly");
  const [includePsRevenue, setIncludePsRevenue] = useState(
    "Do Not Include PS Revenue"
  );
  const [cola, setCola] = useState("0.0");
  const [formInitialized, setFormInitialized] = useState(false);

  const [termError, setTermError] = useState<string[] | null>(null);
  const [termSuccess, setTermSuccess] = useState<string | null>(null);
  const [adjustmentError, setAdjustmentError] = useState<string[] | null>(null);
  const [adjustmentSuccess, setAdjustmentSuccess] = useState<string | null>(
    null
  );

  if (
    !authorized ||
    projectLoading ||
    pricingsLoading ||
    servicesLoading ||
    !project
  ) {
    return <ScopeStackSpinner />;
  }

  const attr = (project?.data?.attributes as any) || {};
  const currencyUnit = attr["field-labels"]?.currency_unit || "$";
  const totalMonths =
    parseFloat((pricingsData as any)?.meta?.["contract-months"]) || 0;

  if (!formInitialized) {
    setTerms(String(attr["mrr-terms"] || ""));
    setStartDate(attr["service-start-date"] || "");
    setBillingFrequency(
      capitalizeFirstLetter(attr["recurring-billing-frequency"] || "monthly")
    );
    setIncludePsRevenue(
      splitPascalCase(
        snakeToPascalCase(attr["include-ps-revenue-in-mrr"] || "do_not_include")
      )
    );
    setCola((parseFloat(attr["cola"]) * 100).toFixed(2) || "0.0");
    setFormInitialized(true);
  }

  const allPricings: any[] = (pricingsData?.data as any[]) || [];
  const recurringPricings = allPricings.filter(
    (p) => p.attributes["billing-frequency"] !== "one_time"
  );

  const allServiceRecords: any[] = (servicesData?.data as any[]) || [];
  const includedRecords: any[] = (servicesData?.included as any[]) || [];

  const activeServices = allServiceRecords.filter(
    (s) => s.attributes.active === true
  );

  const activeSubservices = includedRecords.filter(
    (item) =>
      item.type === "project-subservices" && item.attributes.active === true
  );

  // Build ordered list: service, then its subservices, matched to pricings
  const rows: MsPricingRow[] = [];
  const adjustmentValue = parseFloat(String(attr["mrr-adjustment"] || "0"));

  activeServices.forEach((service) => {
    // Find matching pricing for this service
    const servicePricing = recurringPricings.find(
      (p) => p.id.split("-")[2] === String(service.id)
    );

    if (servicePricing) {
      rows.push({
        id: `svc-${service.id}`,
        service: service.attributes.name,
        quantity: service.attributes.quantity || 0,
        monthlyRevenue: parseFloat(servicePricing.attributes.mrr) || 0,
        monthlyCost: parseFloat(servicePricing.attributes.mrc) || 0,
        rowType: "service",
      });
    }

    // Find subservices for this service
    const subRels = service.relationships?.["project-subservices"]?.data || [];
    for (const rel of subRels) {
      const sub = activeSubservices.find(
        (s) => String(s.id) === String(rel.id)
      );
      if (!sub) continue;

      const subPricing = recurringPricings.find(
        (p) => p.id.split("-")[2] === String(sub.id)
      );
      if (subPricing) {
        rows.push({
          id: `sub-${sub.id}`,
          service: sub.attributes.name,
          quantity: sub.attributes.quantity || 0,
          monthlyRevenue: parseFloat(subPricing.attributes.mrr) || 0,
          monthlyCost: parseFloat(subPricing.attributes.mrc) || 0,
          rowType: "subservice",
        });
      }
    }
  });

  // MRR Adjustment row
  if (recurringPricings.length > 0) {
    rows.push({
      id: "adjustment",
      service: "MRR Adjustment",
      quantity: 0,
      monthlyRevenue: adjustmentValue,
      monthlyCost: 0,
      rowType: "adjustment",
    });
  }

  // Totals
  const totalRevenue = rows
    .filter((r) => r.rowType === "service" || r.rowType === "subservice")
    .reduce((sum, r) => sum + r.monthlyRevenue, adjustmentValue);
  const totalCost = rows
    .filter((r) => r.rowType === "service" || r.rowType === "subservice")
    .reduce((sum, r) => sum + r.monthlyCost, 0);

  const tcvRevenue = totalRevenue * totalMonths;
  const tcvCost = totalCost * totalMonths;
  const profit = tcvRevenue - tcvCost;
  const margin = tcvRevenue !== 0 ? (profit / tcvRevenue) * 100 : 0;

  rows.push({
    id: "totalMrr",
    service: "TOTAL MRR",
    quantity: 0,
    monthlyRevenue: totalRevenue,
    monthlyCost: totalCost,
    rowType: "totalMrr",
  });

  rows.push({
    id: "tcv",
    service: "TOTAL CONTRACT VALUE",
    quantity: 0,
    monthlyRevenue: tcvRevenue,
    monthlyCost: tcvCost,
    rowType: "tcv",
  });

  rows.push({
    id: "profit",
    service: "PROFIT",
    quantity: 0,
    monthlyRevenue: profit,
    monthlyCost: 0,
    rowType: "profit",
  });

  rows.push({
    id: "margin",
    service: "MARGIN",
    quantity: 0,
    monthlyRevenue: margin,
    monthlyCost: 0,
    rowType: "margin",
  });

  const handleSubmitTerm = async () => {
    try {
      await updateProject({
        slug: accountSlug,
        id: Number(projectId),
        body: {
          data: {
            type: "projects",
            id: projectId,
            attributes: {
              cola: parseFloat(cola) / 100,
              "mrr-terms": parseFloat(terms),
              "recurring-billing-frequency": billingFrequency.toLowerCase(),
              "service-start-date": startDate,
              "include-ps-revenue-in-mrr": toSnakeCase(
                includePsRevenue.toLowerCase()
              ),
            },
            relationships: {
              account: { data: { type: "accounts", id: account_id } },
            },
          } as any,
        },
      }).unwrap();
      setTermSuccess("Managed Services billing and payment details updated");
    } catch (err: any) {
      setTermError(
        formatUnprocessibleResponse(
          err,
          "pricing details"
        ) as unknown as string[]
      );
    }
  };

  return (
    <div className="tab-content">
      <div className="tab-pane active">
        <Show
          rows={rows}
          currencyUnit={currencyUnit}
          isLoading={pricingsLoading || servicesLoading}
          insightPupmEnabled={insightPupmEnabled}
          pricingPrivilege={pricingPrivilege}
          terms={terms}
          setTerms={setTerms}
          startDate={startDate}
          setStartDate={setStartDate}
          billingFrequency={billingFrequency}
          setBillingFrequency={setBillingFrequency}
          includePsRevenue={includePsRevenue}
          setIncludePsRevenue={setIncludePsRevenue}
          cola={cola}
          setCola={setCola}
          projectId={projectId}
          onSubmitTerm={handleSubmitTerm}
          termError={termError}
          termSuccess={termSuccess}
          adjustmentError={adjustmentError}
          adjustmentSuccess={adjustmentSuccess}
          setTermSuccess={setTermSuccess}
          setAdjustmentSuccess={setAdjustmentSuccess}
          setTermError={setTermError}
          setAdjustmentError={setAdjustmentError}
        />
      </div>
    </div>
  );
};

export default MsPricingV2Mspa;
